import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlowNav } from "../components/FlowNav";
import {
  getHospitalityObservationOrder,
  getHospitalityRequiredModulesBeforeResources,
  resolveHospitalityContactNow,
  resolveHospitalityFromAnswers,
  resolveHospitalityPossibleMinor,
  resolveHospitalityProtocolOnly,
  resolveHospitalitySkipAll,
} from "../hospitality/resolveHospitality";
import type { HospitalityView } from "../hospitality/types";
import type { IndicatorAnswer, ResolvedRouting } from "../help/types";
import { useSession } from "../state/SessionProvider";

function moduleTitleKey(id: string): string {
  return id.endsWith(".title") ? id : `${id}.title`;
}

function moduleBodyKey(id: string): string {
  if (id === "hospitality.no_guest_pii") {
    return "hospitality.no_guest_pii.body";
  }
  return `${id}.body`;
}

/**
 * Hospitality professional mode — notice, follow workplace protocol, get support.
 * No case file, guest PII, or investigation. Memory-only observations.
 */
export function HospitalityPage() {
  const navigate = useNavigate();
  const {
    translate,
    hospitalityAnswers,
    setHospitalityAnswer,
    clearHospitalityAnswers,
    setResourceCategories,
    setResourceReturnPath,
    hospitalityBookmark,
    setHospitalityBookmark,
    clearQrContext,
  } = useSession();

  const [view, setView] = useState<HospitalityView>("hub");
  const [history, setHistory] = useState<HospitalityView[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [optionsRouting, setOptionsRouting] = useState<ResolvedRouting | null>(
    null,
  );
  const [pendingResourceCategories, setPendingResourceCategories] = useState<
    string[] | null
  >(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const restored = useRef(false);

  const observationOrder = getHospitalityObservationOrder();

  useEffect(() => {
    if (restored.current || !hospitalityBookmark) return;
    restored.current = true;
    setView(hospitalityBookmark.view as HospitalityView);
    setQuestionIndex(hospitalityBookmark.questionIndex);
    setHistory(hospitalityBookmark.history as HospitalityView[]);
    setHospitalityBookmark(null);
  }, [hospitalityBookmark, setHospitalityBookmark]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [view, questionIndex]);

  function pushView(next: HospitalityView) {
    setHistory((h) => [...h, view]);
    setView(next);
  }

  function goBack() {
    if (view === "observations" && questionIndex > 0) {
      setQuestionIndex((i) => i - 1);
      return;
    }
    const prev = history[history.length - 1] ?? "hub";
    setHistory((h) => h.slice(0, -1));
    setView(prev);
  }

  function clearHospitalityFlow() {
    clearHospitalityAnswers();
    setOptionsRouting(null);
    setActiveModules([]);
    setPendingResourceCategories(null);
    setHistory([]);
    setQuestionIndex(0);
    setView("hub");
    setHospitalityBookmark(null);
    setResourceCategories(null);
  }

  function goBackToLanding() {
    clearHospitalityFlow();
    clearQrContext();
    navigate("/");
  }

  function goHome() {
    clearHospitalityFlow();
    clearQrContext();
    navigate("/");
  }

  function goTalk(categories: string[]) {
    setPendingResourceCategories(categories);
    setActiveModules(getHospitalityRequiredModulesBeforeResources());
    pushView("gate");
  }

  function continueToResources() {
    const cats =
      pendingResourceCategories ??
      resolveHospitalityContactNow().resourceCategories;
    setHospitalityBookmark({
      view,
      questionIndex,
      history,
    });
    setResourceReturnPath("/pro/hospitality");
    setResourceCategories(cats);
    navigate("/resources");
  }

  function goOptions(routing: ResolvedRouting) {
    setOptionsRouting(routing);
    pushView("options");
  }

  function openModules(modules: string[]) {
    setActiveModules(modules);
    pushView("modules");
  }

  function startObservations() {
    setQuestionIndex(0);
    pushView("observations");
  }

  function recordAndAdvance(id: string, value: IndicatorAnswer) {
    setHospitalityAnswer(id, value);
    const nextAnswers = { ...hospitalityAnswers, [id]: value };
    if (questionIndex >= observationOrder.length - 1) {
      goOptions(resolveHospitalityFromAnswers(nextAnswers));
      return;
    }
    setQuestionIndex((i) => i + 1);
  }

  function stopQuestions() {
    clearHospitalityAnswers();
    goOptions(resolveHospitalitySkipAll());
  }

  const routing =
    optionsRouting ?? resolveHospitalityFromAnswers(hospitalityAnswers);
  const nestedNav = <FlowNav onBack={goBack} onHome={goHome} />;

  function observePromptKey(indicatorId: string): string {
    return `hospitality.observe.${indicatorId}`;
  }

  function renderModules(modules: string[]) {
    return modules.map((moduleId) => (
      <article key={moduleId} className="info-module">
        <h2>{translate(moduleTitleKey(moduleId))}</h2>
        <p>{translate(moduleBodyKey(moduleId))}</p>
      </article>
    ));
  }

  if (view === "unsure") {
    return (
      <section className="stack help-flow" aria-labelledby="hosp-heading">
        <h1
          id="hosp-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("page.hospitality.title")}
        </h1>
        <p>{translate("mode.hospitality.unsure.prompt")}</p>
        <p className="muted sme-note">{translate("mode.hospitality.sme_note")}</p>
        <div className="stack">
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => openModules(resolveHospitalityProtocolOnly().modules)}
          >
            <span className="mode-card-label">
              {translate("mode.hospitality.unsure.learn.label")}
            </span>
            <span className="mode-card-blurb">
              {translate("mode.hospitality.unsure.learn.blurb")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => pushView("educate")}
          >
            <span className="mode-card-label">
              {translate("mode.hospitality.unsure.observe.label")}
            </span>
            <span className="mode-card-blurb">
              {translate("mode.hospitality.unsure.observe.blurb")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goOptions(resolveHospitalityProtocolOnly())}
          >
            <span className="mode-card-label">
              {translate("mode.hospitality.unsure.next.label")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() =>
              goTalk(resolveHospitalityContactNow().resourceCategories)
            }
          >
            <span className="mode-card-label">
              {translate("mode.hospitality.unsure.talk.label")}
            </span>
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "educate") {
    return (
      <section className="stack help-flow" aria-labelledby="hosp-heading">
        <h1
          id="hosp-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.hospitality.educate.heading")}
        </h1>
        <p className="muted sme-note">{translate("mode.hospitality.sme_note")}</p>

        <article className="info-module">
          <h2>{translate("module.hospitality_protocol.title")}</h2>
          <p>{translate("module.hospitality_protocol.body")}</p>
        </article>

        <article className="info-module">
          <h2>{translate("module.observation_vs_assumption.title")}</h2>
          <p>{translate("module.observation_vs_assumption.body")}</p>
          <p>
            <strong>
              {translate("mode.hospitality.educate.observation_label")}
            </strong>{" "}
            {translate("mode.hospitality.educate.observation_example")}
          </p>
          <p>
            <strong>
              {translate("mode.hospitality.educate.assumption_label")}
            </strong>{" "}
            {translate("mode.hospitality.educate.assumption_example")}
          </p>
          <p>{translate("mode.hospitality.educate.assumption_limit")}</p>
        </article>

        <article className="info-module">
          <h2>{translate("hospitality.no_guest_pii.title")}</h2>
          <p>{translate("hospitality.no_guest_pii.body")}</p>
        </article>

        <article className="info-module">
          <h2>{translate("mode.hospitality.donot.heading")}</h2>
          <p>{translate("mode.hospitality.donot.intro")}</p>
          <ul className="do-not-list">
            <li>{translate("mode.hospitality.donot.confront")}</li>
            <li>{translate("mode.hospitality.donot.follow")}</li>
            <li>{translate("mode.hospitality.donot.search")}</li>
            <li>{translate("mode.hospitality.donot.ids")}</li>
            <li>{translate("mode.hospitality.donot.photo")}</li>
            <li>{translate("mode.hospitality.donot.record")}</li>
            <li>{translate("mode.hospitality.donot.plates")}</li>
            <li>{translate("mode.hospitality.donot.rescue")}</li>
            <li>{translate("mode.hospitality.donot.detain")}</li>
            <li>{translate("mode.hospitality.donot.social")}</li>
            <li>{translate("mode.hospitality.donot.investigate")}</li>
            <li>{translate("mode.hospitality.donot.impersonate")}</li>
          </ul>
        </article>

        <div className="stack">
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={startObservations}
          >
            {translate("mode.hospitality.educate.continue_observe")}
          </button>
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() => goOptions(resolveHospitalityProtocolOnly())}
          >
            {translate("mode.hospitality.secondary.see_next")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "observations") {
    const indicatorId = observationOrder[questionIndex];
    const liveRouting = resolveHospitalityFromAnswers(hospitalityAnswers);
    const talkCategories =
      liveRouting.resourceCategories.length > 0
        ? liveRouting.resourceCategories
        : resolveHospitalityContactNow().resourceCategories;

    return (
      <section className="stack help-flow" aria-labelledby="hosp-heading">
        <h1
          id="hosp-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.hospitality.observe.orientation")}
        </h1>
        <p className="muted">{translate("mode.hospitality.observe.helper")}</p>
        <p className="muted sme-note">{translate("mode.hospitality.sme_note")}</p>

        <div className="question-block">
          <h2 className="question-prompt">
            {translate(observePromptKey(indicatorId))}
          </h2>
          <div
            className="answer-grid"
            role="group"
            aria-label={translate(observePromptKey(indicatorId))}
          >
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "yes")}
            >
              {translate("answer.yes")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "no")}
            >
              {translate("answer.no")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "not_sure")}
            >
              {translate("answer.not_sure")}
            </button>
            <button
              type="button"
              className="mode-link mode-link--quiet"
              onClick={() => recordAndAdvance(indicatorId, "prefer_not")}
            >
              {translate("answer.prefer_not")}
            </button>
          </div>
        </div>

        <div className="stack escape-actions">
          <button
            type="button"
            className="text-action"
            onClick={() => goOptions(liveRouting)}
          >
            {translate("mode.hospitality.secondary.see_next")}
          </button>
          <button
            type="button"
            className="text-action"
            onClick={() => goTalk(talkCategories)}
          >
            {translate("answer.talk_to_someone")}
          </button>
          <button type="button" className="text-action" onClick={stopQuestions}>
            {translate("answer.skip_all")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "gate") {
    return (
      <section className="stack help-flow" aria-labelledby="hosp-heading">
        <h1
          id="hosp-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.hospitality.gate.heading")}
        </h1>
        <p className="muted">{translate("mode.hospitality.gate.intro")}</p>
        <p className="muted sme-note">{translate("mode.hospitality.sme_note")}</p>
        {renderModules(activeModules)}
        <div className="stack">
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={continueToResources}
          >
            {translate("mode.hospitality.gate.continue")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "modules") {
    const modules =
      activeModules.length > 0
        ? activeModules
        : resolveHospitalityProtocolOnly().modules;
    return (
      <section className="stack help-flow" aria-labelledby="hosp-heading">
        <h1
          id="hosp-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.hospitality.modules.heading")}
        </h1>
        <p className="muted sme-note">{translate("mode.hospitality.sme_note")}</p>
        {renderModules(modules)}
        <div className="stack">
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() => goOptions(resolveHospitalityProtocolOnly())}
          >
            {translate("mode.hospitality.secondary.see_next")}
          </button>
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() =>
              goTalk(
                pendingResourceCategories ??
                  resolveHospitalityContactNow().resourceCategories,
              )
            }
          >
            {translate("answer.talk_to_someone")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  if (view === "options") {
    const framingKey = routing.hasAnyYes
      ? "mode.hospitality.options_framing"
      : "mode.hospitality.options_framing_generic";
    return (
      <section className="stack help-flow" aria-labelledby="hosp-heading">
        <h1
          id="hosp-heading"
          className="help-heading"
          ref={headingRef}
          tabIndex={-1}
        >
          {translate("mode.hospitality.options.heading")}
        </h1>
        <p>{translate(framingKey)}</p>
        <p className="muted sme-note">{translate("mode.hospitality.sme_note")}</p>
        <div className="stack">
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => openModules(routing.modules)}
          >
            <span className="mode-card-label">
              {translate("mode.hospitality.options.learn")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => goTalk(routing.resourceCategories)}
          >
            <span className="mode-card-label">
              {translate("mode.hospitality.options.talk")}
            </span>
          </button>
          <button
            type="button"
            className="mode-card mode-link--quiet"
            onClick={() => pushView("educate")}
          >
            <span className="mode-card-label">
              {translate("mode.hospitality.options.observe")}
            </span>
          </button>
          <button
            type="button"
            className="text-action"
            onClick={() => {
              const minor = resolveHospitalityPossibleMinor();
              setPendingResourceCategories(minor.resourceCategories);
              openModules(minor.modules);
            }}
          >
            {translate("mode.hospitality.options.possible_minor")}
          </button>
        </div>
        {nestedNav}
      </section>
    );
  }

  return (
    <section className="stack help-flow" aria-labelledby="hosp-heading">
      <h1
        id="hosp-heading"
        className="help-heading"
        ref={headingRef}
        tabIndex={-1}
      >
        {translate("page.hospitality.title")}
      </h1>
      <p className="muted">{translate("mode.hospitality.awareness")}</p>
      <p className="muted">{translate("mode.hospitality.awareness.disclosure")}</p>
      <p className="muted sme-note">{translate("mode.hospitality.sme_note")}</p>

      <h2 className="mode-heading">{translate("mode.hospitality.hub.prompt")}</h2>

      <div className="stack">
        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => pushView("educate")}
        >
          <span className="mode-card-label">
            {translate("mode.hospitality.hub.understand.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.hospitality.hub.understand.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => {
            clearHospitalityAnswers();
            goOptions(resolveHospitalityProtocolOnly());
          }}
        >
          <span className="mode-card-label">
            {translate("mode.hospitality.hub.next.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.hospitality.hub.next.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() =>
            goTalk(resolveHospitalityContactNow().resourceCategories)
          }
        >
          <span className="mode-card-label">
            {translate("mode.hospitality.hub.talk.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.hospitality.hub.talk.blurb")}
          </span>
        </button>

        <button
          type="button"
          className="mode-card mode-link--quiet"
          onClick={() => pushView("unsure")}
        >
          <span className="mode-card-label">
            {translate("mode.hospitality.hub.unsure.label")}
          </span>
          <span className="mode-card-blurb">
            {translate("mode.hospitality.hub.unsure.blurb")}
          </span>
        </button>
      </div>

      <FlowNav onBack={goBackToLanding} />
    </section>
  );
}
